import { type VoidFunctionComponent } from 'react';
import { useState } from 'react';
import type Stripe from 'stripe-latest';
import { Modal } from 'react-bootstrap';
import { usePlansContext } from './PlansContext';
import Card from '../../../generic/Card/Card';
import LimitsComponent from '../Limits';
import FeaturesComponent from '../Features';
import styles from './styles.module.less';
import { BUTTON_SIZE, BUTTON_TYPE, LoaderButton } from '../../../generic/Button';
import { useUpdatePlan } from '../../../../hooks/subscriptions/useUpdatePlan';
import { FREE_PLAN_NAME } from '../../../../../shared/subscriptions';
import { useSynchronizeSubscription } from '../../../../hooks/subscriptions/useSynchronizeSubscription';
import { useOnMount } from '../../../../hooks';
import Loader from '../../../generic/Loader';
import { useAddToast } from '../../../../contexts/ToastContext';
import { LONG_DATE_FORMAT } from '../../../../helpers/date';
import ChurnFormModal from './ChurnFormModal';
import { getBaseOrgProperties, track } from '../../../../helpers/analytics';

type MessageModalProps = {
  onHide: () => void;
};

const MessageModal: VoidFunctionComponent<MessageModalProps> = ({
  onHide,
}: MessageModalProps) => {
  const {
    isLoading,
    mutate: synchronizeSubscription,
  } = useSynchronizeSubscription();

  const {
    currentPlan,
    getPlanName,
    isLoading: isLoadingCurrentPlan,
  } = usePlansContext();

  useOnMount(synchronizeSubscription);

  let text = `You are now subscribed to plan: '${getPlanName(currentPlan?.plan)}'.`;

  const currentPlanEndDate = currentPlan?.endDate;
  if (currentPlan?.nextScheduledPriceId && currentPlanEndDate) {
    const date = new Date(currentPlanEndDate * 1000);
    text = `Your current plan '${getPlanName(currentPlan?.plan)}' is scheduled to end on ${LONG_DATE_FORMAT.format(date)}.`;
  }

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {
            isLoading || isLoadingCurrentPlan
              ? 'Updating...'
              : 'Your Subscription has been cancelled'
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {
          isLoading || isLoadingCurrentPlan
            ? <Loader />
            : <p>{text}</p>
        }
      </Modal.Body>
    </Modal>
  );
};

type GetButtonTextParams = {
  endDate?: number;
  isCurrentPlan: boolean;
  isNextPlan: boolean;
};

const getButtonText = ({ endDate, isCurrentPlan, isNextPlan }: GetButtonTextParams) => {
  if (isCurrentPlan) {
    return 'Current Plan';
  }

  if (isNextPlan && endDate) {
    return `Your subscription starts on ${LONG_DATE_FORMAT.format(new Date(endDate * 1000))}`;
  }

  return 'Downgrade Plan';
};

/**
 * Renders the price component specifically for the FREE plan.
 * Displays information related to the free pricing tier.
 */
const DisplayFreePlan: VoidFunctionComponent = () => {
  const { currentPlan, freePrice: price } = usePlansContext();

  const [
    shouldShowModal,
    setShouldShowModal,
  ] = useState<boolean>(false);

  const [
    shouldShowChurnModal,
    setShouldShowChurnModal,
  ] = useState<boolean>(false);

  const {
    isLoading: isUpdating,
    mutateAsync: updatePlan,
  } = useUpdatePlan();

  const addToast = useAddToast();

  if (!currentPlan) {
    return null;
  }

  const isNextPlan = currentPlan.nextScheduledPriceId === price.id;
  const isSelectedCurrent = currentPlan.plan === price.id || currentPlan.plan === FREE_PLAN_NAME;

  const onChurnFormSubmit = () => {
    setShouldShowChurnModal(false);
    track(`Downgrade Confirmed - Churn Modal`, getBaseOrgProperties());
    updatePlan(price.id)
      .then(() => setShouldShowModal(true))
      .catch((e) => {
        addToast({ type: 'error', content: `An unexpected error happened. Error message: ${e.message}` });
      });
  };

  return (
    <>
      {
        shouldShowModal &&
        <MessageModal
          onHide={() => setShouldShowModal(false)}
        />
      }
      <ChurnFormModal
        isShown={shouldShowChurnModal}
        onHide={() => {
          track(`Downgrade Cancelled - Churn Modal`, getBaseOrgProperties());
          setShouldShowChurnModal(false);
        }}
        onFormSubmit={onChurnFormSubmit}
      />
      <span className={styles.price}>$0/month</span>
      <Card
        isFullWidth
        padding="xs"
        variant="outline"
      >
        <Card.Content>
          <span className={styles.priceTierDescription}>One size fits all</span>
        </Card.Content>
      </Card>
      <LoaderButton
        size={BUTTON_SIZE.LARGE}
        type={isSelectedCurrent || isNextPlan ? BUTTON_TYPE.SECONDARY : BUTTON_TYPE.ERROR}
        isFullWidth
        isLoading={isUpdating}
        onClick={() => setShouldShowChurnModal(true)}
        isDisabled={isSelectedCurrent || isNextPlan}
      >
        { getButtonText({ endDate: currentPlan.endDate, isCurrentPlan: isSelectedCurrent, isNextPlan }) }
      </LoaderButton>

      <LimitsComponent product={price.product as Stripe.Product} />
      <FeaturesComponent product={price.product as Stripe.Product} />
    </>
  );
};

export default DisplayFreePlan;
